import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Edit3, Plus, Save, ShieldCheck, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";

const emptyUser = {
  name: "",
  email: "",
  password: "",
  role: "viewer",
  can_access_panel: true,
  is_active: true,
};

const Field = ({ label, value, onChange, type = "text", placeholder = "" }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</label>
    <input
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full mt-1 px-3 py-2 text-sm bg-secondary/30 rounded-lg outline-none focus:bg-white border border-transparent focus:border-primary"
    />
  </div>
);

const Toggle = ({ label, checked, onChange }) => (
  <label className="flex items-center gap-2 text-sm text-primary font-medium cursor-pointer select-none">
    <input type="checkbox" checked={Boolean(checked)} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-primary" />
    {label}
  </label>
);

const AdminUsers = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyUser);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Erro ao carregar usuários");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startCreate = () => {
    setEditingId("new");
    setForm(emptyUser);
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      name: item.name || "",
      email: item.email || "",
      password: "",
      role: item.role || "viewer",
      can_access_panel: Boolean(item.can_access_panel),
      is_active: item.is_active !== false,
    });
  };

  const cancel = () => {
    setEditingId(null);
    setForm(emptyUser);
  };

  const saveUser = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        can_access_panel: form.role === "admin" ? true : Boolean(form.can_access_panel),
      };
      if (editingId !== "new" && !payload.password) delete payload.password;

      if (editingId === "new") {
        if (!payload.password) {
          toast.error("Informe uma senha para criar o usuário");
          setSaving(false);
          return;
        }
        await api.post("/users", payload);
        toast.success("Usuário criado!");
      } else {
        await api.patch(`/users/${editingId}`, payload);
        toast.success("Usuário atualizado!");
      }
      cancel();
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Erro ao salvar usuário");
    } finally {
      setSaving(false);
    }
  };

  const removeUser = async (item) => {
    if (!window.confirm(`Excluir o usuário ${item.email}?`)) return;
    try {
      await api.delete(`/users/${item.id}`);
      toast.success("Usuário excluído!");
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Erro ao excluir usuário");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-users-page">
      <header className="bg-white border-b border-border px-6 lg:px-10 py-5 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Painel Admin</div>
          <h1 className="font-display text-3xl text-primary font-semibold mt-1">Usuários</h1>
          <p className="text-sm text-muted-foreground mt-1">Crie usuários e defina quem pode acessar o painel financeiro.</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/admin" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-border bg-white hover:bg-secondary/40 text-sm font-medium">
            <ArrowLeft className="w-4 h-4" /> Voltar ao admin
          </Link>
          <button onClick={handleLogout} className="inline-flex px-4 py-2 rounded-xl border border-border bg-white hover:bg-secondary/40 text-sm font-medium">Sair</button>
        </div>
      </header>

      <main className="px-6 lg:px-10 py-8 max-w-6xl space-y-6">
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-white border border-border rounded-2xl p-5">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Total</div>
            <div className="font-display text-3xl text-primary font-semibold mt-2">{users.length}</div>
          </div>
          <div className="bg-white border border-border rounded-2xl p-5">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Com acesso ao painel</div>
            <div className="font-display text-3xl text-primary font-semibold mt-2">{users.filter((u) => u.can_access_panel || u.role === "admin").length}</div>
          </div>
          <div className="bg-white border border-border rounded-2xl p-5 flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center"><ShieldCheck className="w-5 h-5" /></div>
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Admin logado</div>
              <div className="text-sm font-medium text-primary">{user?.email}</div>
            </div>
          </div>
        </div>

        {editingId && (
          <section className="bg-white border border-border rounded-2xl p-6 space-y-5">
            <div className="flex items-center justify-between gap-4">
              <h2 className="font-display text-xl text-primary">{editingId === "new" ? "Criar usuário" : "Editar usuário"}</h2>
              <button onClick={cancel} className="text-sm text-muted-foreground hover:text-primary">Cancelar</button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Nome" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} />
              <Field label="E-mail" type="email" value={form.email} onChange={(v) => setForm((f) => ({ ...f, email: v }))} />
              <Field label={editingId === "new" ? "Senha" : "Nova senha (opcional)"} type="password" value={form.password} onChange={(v) => setForm((f) => ({ ...f, password: v }))} />
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground">Tipo de usuário</label>
                <select
                  value={form.role}
                  onChange={(e) => setForm((f) => ({ ...f, role: e.target.value, can_access_panel: e.target.value === "admin" ? true : f.can_access_panel }))}
                  className="w-full mt-1 px-3 py-2 text-sm bg-secondary/30 rounded-lg outline-none focus:bg-white border border-transparent focus:border-primary"
                >
                  <option value="admin">Administrador</option>
                  <option value="viewer">Usuário comum</option>
                </select>
              </div>
            </div>
            <div className="flex flex-wrap gap-5">
              <Toggle label="Usuário ativo" checked={form.is_active} onChange={(v) => setForm((f) => ({ ...f, is_active: v }))} />
              <Toggle label="Permitir acesso ao painel financeiro" checked={form.role === "admin" || form.can_access_panel} onChange={(v) => setForm((f) => ({ ...f, can_access_panel: v }))} />
            </div>
            <button onClick={saveUser} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-accent text-accent-foreground font-medium hover:-translate-y-0.5 transition disabled:opacity-60">
              <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar usuário"}
            </button>
          </section>
        )}

        <section className="bg-white border border-border rounded-2xl p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <h2 className="font-display text-xl text-primary flex items-center gap-2"><Users className="w-5 h-5" /> Lista de usuários</h2>
            <button onClick={startCreate} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90">
              <Plus className="w-4 h-4" /> Novo usuário
            </button>
          </div>

          {loading ? (
            <div className="text-muted-foreground py-10 text-center">Carregando usuários…</div>
          ) : users.length === 0 ? (
            <div className="text-muted-foreground py-10 text-center">Nenhum usuário cadastrado.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground border-b border-border">
                    <th className="py-3 pr-4">Nome</th>
                    <th className="py-3 pr-4">E-mail</th>
                    <th className="py-3 pr-4">Tipo</th>
                    <th className="py-3 pr-4">Painel financeiro</th>
                    <th className="py-3 pr-4">Status</th>
                    <th className="py-3 pr-4 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((item) => (
                    <tr key={item.id} className="border-b border-border/60 last:border-0">
                      <td className="py-3 pr-4 font-medium text-primary">{item.name || "-"}</td>
                      <td className="py-3 pr-4">{item.email}</td>
                      <td className="py-3 pr-4">{item.role === "admin" ? "Administrador" : "Usuário comum"}</td>
                      <td className="py-3 pr-4">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${(item.can_access_panel || item.role === "admin") ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                          {(item.can_access_panel || item.role === "admin") ? "Permitido" : "Bloqueado"}
                        </span>
                      </td>
                      <td className="py-3 pr-4">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${item.is_active !== false ? "bg-blue-100 text-blue-700" : "bg-red-100 text-red-700"}`}>
                          {item.is_active !== false ? "Ativo" : "Inativo"}
                        </span>
                      </td>
                      <td className="py-3 pr-4">
                        <div className="flex justify-end gap-2">
                          <button onClick={() => startEdit(item)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-border hover:border-accent text-primary text-xs"><Edit3 className="w-3 h-3" /> Editar</button>
                          <button onClick={() => removeUser(item)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-border text-destructive text-xs"><Trash2 className="w-3 h-3" /> Excluir</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default AdminUsers;
